// Signup/SignupForm.jsx
// Modern signup form component with enhanced UX and professional design

import React, { useState } from 'react';
import { styles } from './styles';
import { UserIcon, EmailIcon, LockIcon, ErrorIcon } from '../../components/Icons';

const SignupForm = ({ 
  formData, 
  onFieldUpdate, 
  validationErrors, 
  loading, 
  onSubmit 
}) => {
  const [focusedField, setFocusedField] = useState(null);

  const handleInputChange = (field) => (e) => {
    onFieldUpdate(field, e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const getInputStyle = (fieldName, isError = false) => ({
    ...styles.input,
    ...(focusedField === fieldName ? styles.inputFocused : {}),
    ...(isError ? styles.inputError : {})
  });

  const getNameInputStyle = (fieldName, isError = false) => ({
    ...styles.nameInput,
    ...(focusedField === fieldName ? styles.inputFocused : {}),
    ...(isError ? styles.inputError : {})
  });

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      {/* Name Input Group */}
      <div style={styles.nameInputsContainer}>
        <div style={styles.nameInputGroup}>
          <label style={styles.inputLabel} htmlFor="firstName">
            First Name
          </label>
          <div style={styles.inputWrapper}>
            <UserIcon 
              size={20} 
              color="#666" 
              style={styles.inputIcon}
            />
            <input
              id="firstName"
              type="text"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleInputChange('firstName')}
              onFocus={() => setFocusedField('firstName')}
              onBlur={() => setFocusedField(null)}
              style={getNameInputStyle('firstName', !!validationErrors.firstName)}
              className="signup-input"
              disabled={loading}
              required
              autoComplete="given-name"
            />
          </div>
          {validationErrors.firstName && (
            <div style={styles.validationMessage}>
              <ErrorIcon size={14} style={styles.validationIcon} />
              {validationErrors.firstName}
            </div>
          )}
        </div>
        
        <div style={styles.nameInputGroup}>
          <label style={styles.inputLabel} htmlFor="lastName">
            Last Name
          </label>
          <div style={styles.inputWrapper}>
            <UserIcon 
              size={20} 
              color="#666" 
              style={styles.inputIcon}
            />
            <input
              id="lastName"
              type="text"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleInputChange('lastName')}
              onFocus={() => setFocusedField('lastName')}
              onBlur={() => setFocusedField(null)}
              style={getNameInputStyle('lastName', !!validationErrors.lastName)}
              className="signup-input"
              disabled={loading}
              required
              autoComplete="family-name"
            />
          </div>
          {validationErrors.lastName && (
            <div style={styles.validationMessage}>
              <ErrorIcon size={14} style={styles.validationIcon} />
              {validationErrors.lastName}
            </div>
          )}
        </div>
      </div>

      {/* Email Input Group */}
      <div style={styles.inputGroup}>
        <label style={styles.inputLabel} htmlFor="email">
          Email Address
        </label>
        <div style={styles.inputWrapper}>
          <EmailIcon 
            size={20} 
            color="#666" 
            style={styles.inputIcon}
          />
          <input
            id="email"
            type="email"
            placeholder="ulid@ilstu.edu"
            value={formData.email}
            onChange={handleInputChange('email')}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            style={getInputStyle('email', !!validationErrors.email)}
            className="signup-input"
            disabled={loading}
            required
            autoComplete="email"
          />
        </div>
        {validationErrors.email && (
          <div style={styles.validationMessage}>
            <ErrorIcon size={14} style={styles.validationIcon} />
            {validationErrors.email}
          </div>
        )}
      </div>

      {/* Password Input Group */}
      <div style={styles.inputGroup}>
        <label style={styles.inputLabel} htmlFor="password">
          Password
        </label>
        <div style={styles.inputWrapper}>
          <LockIcon 
            size={20} 
            color="#666" 
            style={styles.inputIcon}
          />
          <input
            id="password"
            type="password"
            placeholder="Create secure password (8+ chars)"
            value={formData.password}
            onChange={handleInputChange('password')}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            style={getInputStyle('password', !!validationErrors.password)}
            className="signup-input"
            disabled={loading}
            required
            autoComplete="new-password"
          />
        </div>
        {validationErrors.password && (
          <div style={styles.validationMessage}>
            <ErrorIcon size={14} style={styles.validationIcon} />
            {validationErrors.password}
          </div>
        )}
      </div>

      {/* Confirm Password Input Group */}
      <div style={styles.inputGroup}>
        <label style={styles.inputLabel} htmlFor="confirmPassword">
          Confirm Password
        </label>
        <div style={styles.inputWrapper}>
          <LockIcon 
            size={20} 
            color="#666" 
            style={styles.inputIcon}
          />
          <input
            id="confirmPassword"
            type="password"
            placeholder="Confirm password"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            onFocus={() => setFocusedField('confirmPassword')}
            onBlur={() => setFocusedField(null)}
            style={getInputStyle('confirmPassword', !!validationErrors.confirmPassword)}
            className="signup-input"
            disabled={loading}
            required
            autoComplete="new-password"
          />
        </div>
        {validationErrors.confirmPassword && (
          <div style={styles.validationMessage}>
            <ErrorIcon size={14} style={styles.validationIcon} />
            {validationErrors.confirmPassword}
          </div>
        )}
      </div>

      {/* General Error Message */}
      {validationErrors.general && (
        <div style={styles.validationMessage}>
          <ErrorIcon size={14} style={styles.validationIcon} />
          {validationErrors.general}
        </div>
      )}

      {/* Submit Button with Loading State */}
      <button
        type="submit"
        style={{
          ...styles.submitButton,
          ...(loading ? styles.submitButtonDisabled : styles.submitButtonEnabled)
        }}
        className="signup-button"
        disabled={loading}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 24px rgba(206, 17, 38, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 16px rgba(206, 17, 38, 0.4)';
          }
        }}
      >
        {loading && (
          <div style={styles.loadingSpinner} className="loading-spinner"></div>
        )}
        {loading ? 'Creating Your Account...' : 'Create Account'}
      </button>
    </form>
  );
};

export default SignupForm;